import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { globalVars } from "../../global/GlobalVars.js";
import * as language from "../../utils/language.js";
import * as websocket from "../../utils/communication.js";

//========================================================================
// Tabelle
const AlarmConfig = observer(({ style, scaleFactor }) => {
  const [alarmConfig, setAlarmConfig] = useState([]);
  const [sortOrder, setSortOrder] = useState("asc"); // Zustand für Sortierreihenfolge

  useEffect(() => {
    websocket.getAlarmConfig(globalVars.ws);
  }, []);

  useEffect(() => {
    const alarmConfig = {
      id: "Alarm_bBDW00AktKohlPulvDosA13Stoe",
      text: [
        "BDW00 - Gemeinsamme Einrichtungen - SM Aktivkohle Pulverdosierung Anlage 1-3 Störung",
        "",
        "",
      ],
      severity: "fault",
      contact1: 0,
      contact2: 0,
      email: 0,
      sms: 0,
    };

    // Alarmkonfiguration nach Severity und Text sortieren
    let sortLanguageIndex;

    // Festlegen des Indexes für die gewählte Sprache
    if (globalVars.language === "de") {
      sortLanguageIndex = 0;
    } else if (globalVars.language === "it") {
      sortLanguageIndex = 1;
    } else if (globalVars.language === "en") {
      sortLanguageIndex = 2;
    } else {
      sortLanguageIndex = 0; // Fallback
    }

    // Kopie von alarmConfig erstellen
    let newAlarmConfig = [...globalVars.alarmConfig];

    // Sortieren nach Severity und anschließend nach Text in der gewählten Sprache
    newAlarmConfig.sort((a, b) => {
      // Sortierlogik für severity (z.B. "alarm" < "fault" < "warning")
      const severityOrder = { alarm: 1, fault: 2, warning: 3 };
      const severityComparison =
        (severityOrder[a.severity] || 9999) -
        (severityOrder[b.severity] || 9999);

      // Wenn Severity gleich ist, nach Text in der gewünschten Sprache sortieren
      if (severityComparison === 0) {
        return a.text[sortLanguageIndex].localeCompare(
          b.text[sortLanguageIndex]
        );
      }

      return severityComparison;
    });

    setAlarmConfig(newAlarmConfig);
  }, [globalVars.alarmConfig]);

  const handleCheckboxChange = (index, field) => {
    const updatedConfig = [...alarmConfig];
    updatedConfig[index][field] = !updatedConfig[index][field];
    setAlarmConfig(updatedConfig);
  };

  const handleSave = () => {
    websocket.updateAlarmConfig(globalVars.ws, alarmConfig);
  };

  // Sortierlogik
  const sortAlarmConfig = () => {
    const sortedConfig = [...alarmConfig].sort((a, b) => {
      if (a.text < b.text) return sortOrder === "asc" ? -1 : 1;
      if (a.text > b.text) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    setAlarmConfig(sortedConfig);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc"); // Sortierreihenfolge umschalten
  };

  //========================================================================
  // Styles
  const commonStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
  };

  const columnStyles = {
    name: {
      width: "60%",
      ...commonStyles,
      textAlign: "left",
      paddingLeft: "10px",
    },
    contact1: { width: "10%", ...commonStyles },
    contact2: { width: "10%", ...commonStyles },
    sms: { width: "10%", ...commonStyles },
    email: { width: "10%", ...commonStyles },
  };

  const checkboxStyles = {
    width: `calc(${scaleFactor} * 30px)`,
    height: `calc(${scaleFactor} * 30px)`,
  };

  return (
    <div style={{ ...style }}>
      {/* ######################################################################################################## */}
      {/* ################################################ Spaltenname ########################################### */}
      <div
        style={{
          position: "absolute",
          width: "92.5%",
          height: "4%",
          top: "0px",
          alignItems: "start",
          display: "flex",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            height: `calc(${scaleFactor} * 40px)`,
          }}
        >
          <thead
            style={{
              cursor: "pointer",
              backgroundColor: "#636363",
              fontWeight: "bold",
              padding: "10px",
              userSelect: "none",
            }}
          >
            <tr>
              <th style={columnStyles.name} onClick={sortAlarmConfig}>
                {language.displayText([
                  "Alarmtext",
                  "testo allarme",
                  "alarmtext",
                ])}
              </th>
              <th style={columnStyles.contact1}>
                {language.displayText(["Kontakt 1", "contatto 1", "contact 1"])}
              </th>
              <th style={columnStyles.contact2}>
                {language.displayText(["Kontakt 2", "contatto 2", "contact 2"])}
              </th>
              <th style={columnStyles.email}>
                {language.displayText(["E-Mail", "E-Mail", "E-Mail"])}
              </th>
              <th style={columnStyles.sms}>
                {language.displayText(["SMS", "SMS", "SMS"])}
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* ######################################################################################################## */}
      {/* ################################################ Alarmkonfiguration #################################### */}
      <div
        className="scroll-container"
        style={{
          position: "absolute",
          width: "92.5%",
          height: `calc(100% - ${scaleFactor} * 40px)`,
          overflowY: "scroll",
          top: `calc(${scaleFactor} * 40px)`,
          scrollbarWidth: "none",
          backgroundColor: "lightgrey",
        }}
      >
        <style>
          {`
            .scroll-container::-webkit-scrollbar {
              display: none; /* For Chrome, Safari, and Opera */
            }
          `}
        </style>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "papayawhip",
          }}
        >
          <tbody>
            {alarmConfig.map((alarm, index) => {
              const rowBackgroundColor =
                {
                  alarm: "#ffaaaa",
                  fault: "#f7ffa6",
                  warning: "#ffd7a6",
                }[alarm.severity] || "white"; // Fallback: Weiß

              return (
                <tr
                  key={index}
                  style={{
                    height: `calc(${scaleFactor} * 35px)`,
                    backgroundColor: rowBackgroundColor,
                  }}
                >
                  <td style={columnStyles.name}>
                    {language.displayText(alarm.text)}
                  </td>
                  <td style={columnStyles.contact1}>
                    <input
                      type="checkbox"
                      style={checkboxStyles}
                      checked={alarm.contact1}
                      onChange={() => handleCheckboxChange(index, "contact1")}
                    />
                  </td>
                  <td style={columnStyles.contact2}>
                    <input
                      type="checkbox"
                      style={checkboxStyles}
                      checked={alarm.contact2}
                      onChange={() => handleCheckboxChange(index, "contact2")}
                    />
                  </td>
                  <td style={columnStyles.email}>
                    <input
                      type="checkbox"
                      style={checkboxStyles}
                      checked={alarm.email}
                      onChange={() => handleCheckboxChange(index, "email")}
                    />
                  </td>
                  <td style={columnStyles.sms}>
                    <input
                      type="checkbox"
                      style={checkboxStyles}
                      checked={alarm.sms}
                      onChange={() => handleCheckboxChange(index, "sms")}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button
        style={{
          position: "absolute",
          top: "0%",
          right: "0%",
          height: `calc(${scaleFactor} * 50px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc(${style.fontSize} * 0.8)`,
        }}
      >
        {language.displayText([
          "Einstellungen SMS",
          "Impostazioni SMS",
          "SMS settings",
        ])}
      </button>
      <button
        style={{
          position: "absolute",
          top: `calc(${scaleFactor} * 60px)`,
          right: "0%",
          height: `calc(${scaleFactor} * 50px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc(${style.fontSize} * 0.8)`,
        }}
      >
        {language.displayText([
          "Einstellungen E-Mail",
          "Impostazioni e-mail",
          "E-mail settings",
        ])}
      </button>
      <button
        style={{
          position: "absolute",
          top: `calc(${scaleFactor} * 120px)`,
          right: "0%",
          height: `calc(${scaleFactor} * 50px)`,
          width: "7%",
          textAlign: "center",
          fontSize: `calc(${style.fontSize} * 0.8)`,
        }}
        onClick={() => handleSave()}
      >
        {language.displayText(["Speichern", "Salva", "Save"])}
      </button>
    </div>
  );
});

export default AlarmConfig;
